import type { IContentBase } from "@/types/content";
import type { ITaskBase, SingleResultData } from "@/types/task";
import type { EditableContentType } from "@/utils/content";
import type { QueryObserverResult, RefetchOptions } from "@tanstack/vue-query";
import type { AnyFn } from "@vueuse/core";

type QuickContentEditData = {
  contentId: string;
  dataPath: string;
  contentType: EditableContentType;
};

export type QuickContentFeatureKey = "post" | "schedule";
type QuickTaskContentFeatures = Record<
  string,
  Partial<
    Record<
      QuickContentFeatureKey,
      {
        enabled: boolean;
        callback?: AnyFn;
      }
    >
  >
>;

export type QuickTaskActionPayload =
  | {
      type: "edit";
      contentId: string;
      dataPath: string;
      contentType: EditableContentType;
    }
  | {
      type: "add-media";
      contentId: string;
    }
  | {
      contentId: string;
      type: "regenerate";
      tone: string;
      dataPath: string;
    };

export const QUICK_TASK_ACTION_EVENT = "editorInput";

export function handleV2QuickTaskAction(
  details: QuickTaskActionPayload,
  target?: EventTarget | null
) {
  if (!details || typeof document === "undefined") return;
  const event = new CustomEvent(QUICK_TASK_ACTION_EVENT, {
    detail: details,
    bubbles: true,
  });
  if (target && target instanceof HTMLElement) {
    target.dispatchEvent(event);
    return;
  }
  document.dispatchEvent(new CustomEvent(QUICK_TASK_ACTION_EVENT, { detail: details }));
}

export const useQuickTask = createSharedComposable(() => {
  // const [isTaskUiOpen, toggleTaskUi] = useToggle(false);
  const router = useRouter();
  const isTaskUiOpen = ref(false);
  const [isTaskUiFullscreen, toggleTaskUiFullscreen] = useToggle(false);
  const currentTaskKey = ref<string>("");
  const newTaskPrompt = ref<string>("");
  const newTaskId = ref<string>("");
  const contentEditData = ref<QuickContentEditData | null>(null);
  const contentFeatures = ref<QuickTaskContentFeatures>({});
  const isScheduleSuccess = ref(false);
  const isPostingSuccess = ref(false);
  const postInfo = reactive({
    isPosting: false,
    taskType: "",
  });

  const {
    data,
    isLoading: isLoadingTask,
    refetch: refetchTask,
  } = useSingleTask(newTaskId);

  const shouldFetchContent = computed(
    () => !!data.value && !!data.value?.contentIds?.length
  );

  const {
    data: contentList,
    isLoading: isLoadingContentList,
    refetch: refetchContentList,
  } = useSingleContentRequest({ taskId: newTaskId }, shouldFetchContent);

  const isLoadingTaskOrContent = computed(
    () => isLoadingTask.value || isLoadingContentList.value
  );

  function toggleTaskUi(value?: boolean | undefined) {
    if (currentSessionInformation.onboardingRequired) {
      router.push({ name: RouteNames.ONBOARDING });
      isTaskUiOpen.value = false;
      return false;
    }
    if (typeof value === "boolean") {
      isTaskUiOpen.value = value;
    }
    isTaskUiOpen.value = !isTaskUiOpen.value;
    return isTaskUiOpen.value;
  }

  function refetch() {
    refetchTask();
    refetchContentList();
  }

  const isComplete = computed(() => {
    if (data?.value?.subTasksExpanded?.length) {
      return (
        data?.value.complete == 1 ||
        data?.value.subTasksExpanded.every((subTask: any) => subTask.complete == 1)
      );
    }
    return data?.value?.complete == 1;
  });

  const noTask = computed(() => {
    if (!data || !data.value) {
      return true;
    }
    const keys = Object.keys(data.value) as (keyof ITaskBase)[];
    if (keys.length === 0) {
      return true;
    }
    if (keys.length === 1 && data.value[keys[0]] === null) {
      return true;
    }
    return false;
  });

  const hasError = computed(() => {
    if (noTask.value) return false;
    return (
      data?.value?.error === 1 ||
      data?.value?.subTasksExpanded?.some(subTask => subTask.error == 1)
    );
  });

  const isCanceled = computed(() => {
    if (noTask.value) return false;
    return (
      data?.value?.canceled == 1 ||
      data?.value?.subTasksExpanded?.some(subTask => subTask.canceled == 1)
    );
  });

  const isQueued = computed(() => {
    if (noTask.value) return false;
    return (
      data?.value?.queued == 1 ||
      data?.value?.subTasksExpanded?.some(subTask => subTask.queued == 1)
    );
  });

  // sub-tasks done but parent is queued
  const hasFalseRunningStatus = computed(() => {
    if (!data.value?.subTasksExpanded?.length) return false;
    const hasRunningSubTasks =
      data.value?.subTasksExpanded?.some(subTask => subTask.queued === 1) ||
      data.value?.subTasksExpanded?.some(subTask => subTask.running === 1);
    if (hasRunningSubTasks) return false;
    return data.value.queued === 1;
  });

  const isRunning = computed(() => {
    if (noTask.value) return false;
    if (hasFalseRunningStatus.value) return false;
    return (
      !isComplete.value &&
      data?.value?.canceled !== 1 &&
      data?.value?.error !== 1 &&
      !isLoadingTaskOrContent.value
    );
  });

  const newTaskSTatus = computed(() => {
    return {
      isComplete: isComplete.value,
      noTask: noTask.value,
      hasError: hasError.value,
      isCanceled: isCanceled.value,
      isQueued: isQueued.value,
      isRunning: isRunning.value,
    };
  });

  function editContent(payload: QuickContentEditData) {
    contentEditData.value = payload;
  }

  function reset() {
    contentEditData.value = null;
    currentTaskKey.value = "";
    newTaskPrompt.value = "";
    newTaskId.value = "";
    contentFeatures.value = {};
    postInfo.isPosting = false;
    postInfo.taskType = "";
  }

  return {
    isTaskUiOpen,
    toggleTaskUi,
    isTaskUiFullscreen,
    currentTaskKey,
    newTaskPrompt,
    newTaskId,
    toggleTaskUiFullscreen,

    // result
    taskResult: data,
    isLoadingTask,
    refetchTask,
    refetchTaskAndContent: refetch,
    isLoadingTaskOrContent,
    contentList,
    isLoadingContentList,
    refetchContentList,
    newTaskSTatus,
    editContent,
    contentEditData,
    contentFeatures,
    postInfo,
    reset,
    isScheduleSuccess,
    isPostingSuccess,
  } satisfies UseQuickTaskReturnType;
});

export type UseQuickTaskReturnType = {
  editContent(payload: QuickContentEditData): void;
  // isRunning: globalThis.ComputedRef<boolean>;
  isTaskUiOpen: globalThis.Ref<boolean>;
  toggleTaskUi: (value?: boolean | undefined) => boolean;
  isTaskUiFullscreen: globalThis.Ref<boolean>;
  toggleTaskUiFullscreen: (value?: boolean | undefined) => boolean;
  currentTaskKey: globalThis.Ref<string>;
  newTaskPrompt: globalThis.Ref<string>;
  newTaskId: globalThis.Ref<string>;
  taskResult: globalThis.Ref<ITaskBase | null> | globalThis.Ref<undefined>;
  isLoadingTask: globalThis.Ref<boolean> | globalThis.Ref<false> | globalThis.Ref<true>;
  refetchTask: (
    options?: RefetchOptions | undefined
  ) => Promise<QueryObserverResult<ITaskBase | null, Error>>;
  refetchTaskAndContent: () => void;
  isLoadingTaskOrContent: globalThis.ComputedRef<boolean>;
  contentList:
    | globalThis.Ref<undefined>
    | globalThis.Ref<IContentBase<SingleResultData>[] | null>;
  isLoadingContentList:
    | globalThis.Ref<boolean>
    | globalThis.Ref<false>
    | globalThis.Ref<true>;
  refetchContentList: (
    options?: RefetchOptions | undefined
  ) => Promise<QueryObserverResult<IContentBase<SingleResultData>[] | null, Error>>;
  contentEditData: globalThis.Ref<{
    contentId: string;
    dataPath: string;
    contentType: EditableContentType;
  } | null>;
  newTaskSTatus: globalThis.ComputedRef<{
    isComplete: boolean;
    noTask: boolean;
    hasError: boolean | undefined;
    isCanceled: boolean | undefined;
    isQueued: boolean | undefined;
    isRunning: boolean;
  }>;
  contentFeatures: globalThis.Ref<QuickTaskContentFeatures>;
  postInfo: {
    isPosting: boolean;
    taskType: string;
  };
  reset(): void;
  isScheduleSuccess: globalThis.Ref<boolean>;
  isPostingSuccess: globalThis.Ref<boolean>;
};
